/**
 * Parser for QuickBooks Desktop "Vendor Balance Detail" / vendor transaction
 * CSV exports.
 *
 * Layout (9 cols, identical shape to the Customer detail report — only the
 * row types differ):
 *   ,Type,Date,Num,Memo,Account,Split,Amount,Balance
 *
 * Row types we recognize:
 *   - "Bill"               → AP bill from a vendor (positive amount)
 *   - "Bill Pmt -Check"    → check written to the vendor (negative amount)
 *
 * The `Split` column carries different meaning per row:
 *   - On a Bill: the *expense* GL code the bill should book against
 *     (e.g. "7060.40 · Water & Sewer"). We use this to populate the
 *     bill's single line item.
 *   - On a Bill Pmt -Check: the *bank* GL code (e.g. "1010 · CHECKING -
 *     US Bank"). We use this to look up which BankAccount the payment
 *     was drawn from.
 *
 * Amount sign convention from QuickBooks:
 *   - Bill            → positive (increases AP)
 *   - Bill Pmt -Check → negative (decreases AP / decreases bank)
 *
 * Downstream we store everything as absolute cents in `amountCents`; the
 * raw `amount` is preserved for verification / debugging.
 */

import { BankAccountKind } from '@/types/models';

export type VendorTxnType = 'Bill' | 'Bill Pmt -Check';

export interface ParsedVendorTxn {
	type: VendorTxnType;
	/** QB "Trans #" — present only in the FULL report layout. The exact join
	 *  key back to the journal export's transactions. */
	transId?: string;
	dateStr: string;
	dateMs: number | null;
	/** QB Num column. For Bills this is typically the vendor's invoice #;
	 *  for Bill Pmt -Check it's the check number. */
	num?: string;
	memo?: string;
	/** AP control account ("2000 · ACCOUNTS PAYABLE") on both row types. */
	account?: string;
	/** Offsetting GL — expense code for Bills, bank code for Bill Pmts. */
	split?: string;
	amount: number;
	amountCents: number;
}

export interface ParsedVendorSection {
	vendorName: string;
	transactions: ParsedVendorTxn[];
	reportedTotal?: number;
}

export interface ParsedVendorCsv {
	sections: ParsedVendorSection[];
	grandTotal?: number;
	errors: string[];
}

const splitCsvLine = (line: string): string[] => {
	const out: string[] = [];
	let cur = '';
	let inQuotes = false;
	for (let i = 0; i < line.length; i++) {
		const c = line[i];
		if (inQuotes) {
			if (c === '"') {
				if (line[i + 1] === '"') { cur += '"'; i++; }
				else { inQuotes = false; }
			} else {
				cur += c;
			}
		} else if (c === '"') {
			inQuotes = true;
		} else if (c === ',') {
			out.push(cur);
			cur = '';
		} else {
			cur += c;
		}
	}
	out.push(cur);
	return out;
};

const parseDate = (s: string): number | null => {
	if (!s) return null;
	const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
	if (!m) return null;
	const [, mm, dd, yy] = m;
	const year = yy.length === 2 ? 2000 + Number(yy) : Number(yy);
	// Business date — midnight UTC, not local midnight (which is off-grid and
	// lands on the previous day east of Greenwich).
	const ms = Date.UTC(year, Number(mm) - 1, Number(dd));
	return isFinite(ms) ? ms : null;
};

const parseAmount = (s: string): number | null => {
	if (s == null || s === '') return null;
	const n = Number(String(s).replace(/[,"]/g, ''));
	return isFinite(n) ? n : null;
};

/**
 * Pull the leading numeric account code out of a Split string. QB exports
 * use either "·" (middle dot) or "•" (bullet) as separators, plus a
 * Windows-cp1252 byte that lands as `�` when the file isn't UTF-8 — match
 * any non-alphanumeric prefix delimiter.
 */
export const splitCode = (split: string | undefined): string | undefined => {
	if (!split) return undefined;
	const m = split.match(/^([\d.]+)/);
	return m ? m[1] : undefined;
};

export interface ParsedBankSplit {
	code: string;
	name: string;
	kind: BankAccountKind;
	institution?: string;
}

const detectBankKind = (s: string): BankAccountKind | null => {
	const u = s.toUpperCase();
	if (/CHECK/.test(u)) return BankAccountKind.Checking;
	if (/SAVING/.test(u)) return BankAccountKind.Savings;
	if (/MONEY\s*MARKET|\bMM\b/.test(u)) return BankAccountKind.MoneyMarket;
	if (/\bCD\b/.test(u)) return BankAccountKind.Cd;
	return null;
};

/**
 * Parse a QB bank Split column like `"1010 � CHECKING - US Bank"` into its
 * constituent bank-account fields. The leading code identifies the cash GL;
 * the trailing label is `"<NAME> - <INSTITUTION>"` in either order, with one
 * side typically containing a kind keyword (CHECKING, SAVINGS, MM, CD).
 */
export const parseBankSplit = (split: string | undefined): ParsedBankSplit | null => {
	if (!split) return null;
	const m = split.match(/^([\d.]+)\s*[^A-Za-z0-9]+\s*(.+)$/);
	if (!m) return null;
	const code = m[1];
	const rest = m[2].trim();
	const parts = rest.split(/\s+-\s+/);
	if (parts.length >= 2) {
		const k0 = detectBankKind(parts[0]);
		const k1 = detectBankKind(parts[1]);
		if (k0 && !k1) {
			return { code, kind: k0, name: parts[0], institution: parts.slice(1).join(' - ') };
		}
		if (k1 && !k0) {
			return { code, kind: k1, name: parts.slice(1).join(' - '), institution: parts[0] };
		}
		return { code, kind: k0 ?? k1 ?? BankAccountKind.Other, name: parts[0], institution: parts.slice(1).join(' - ') };
	}
	return { code, kind: detectBankKind(rest) ?? BankAccountKind.Other, name: rest };
};

interface ColumnLayout {
	type: number;
	date: number;
	num: number;
	transId?: number;
	memo?: number;
	account?: number;
	split?: number;
	amount: number;
}

/** Positional fallback (detailed 9-col layout) for rows before any header. */
const DETAILED_LAYOUT: ColumnLayout = { type: 1, date: 2, num: 3, memo: 4, account: 5, split: 6, amount: 7 };

/**
 * Map columns by header NAME, not position. Vendor Balance Detail comes in a
 * detailed 9-col layout AND a full 37-col layout (leading Trans #, address
 * blocks, no Split column). Hardcoded indexes mis-parsed the full layout —
 * Type landed on the Trans # column and every row errored.
 */
const detectLayout = (cells: string[]): ColumnLayout => {
	const named = new Map<string, number>();
	cells.forEach((c, i) => {
		const key = c.trim();
		if (key && !named.has(key)) named.set(key, i);
	});
	const type = named.get('Type');
	const date = named.get('Date');
	const amount = named.get('Amount');
	if (type == null || date == null || amount == null) return DETAILED_LAYOUT;
	return {
		type,
		date,
		num: named.get('Num') ?? type + 2,
		transId: named.get('Trans #'),
		memo: named.get('Memo'),
		account: named.get('Account'),
		split: named.get('Split'),
		amount,
	};
};

export function parseVendorCsv(text: string): ParsedVendorCsv {
	const out: ParsedVendorCsv = { sections: [], errors: [] };
	const lines = text.split(/\r?\n/);
	let current: ParsedVendorSection | null = null;
	let layout: ColumnLayout = DETAILED_LAYOUT;
	let layoutDetected = false;

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		if (!line.trim()) continue;
		const cells = splitCsvLine(line);
		const label = (cells[0] ?? '').trim();

		// Header row — "Type" sits at col 1 (detailed) or col 2 (full, behind
		// Trans #).
		if (!layoutDetected && cells.some((c) => c.trim() === 'Type') && cells.some((c) => c.trim() === 'Amount')) {
			layout = detectLayout(cells);
			layoutDetected = true;
			continue;
		}
		const type = (cells[layout.type] ?? '').trim();

		if (label === 'TOTAL') {
			out.grandTotal = parseAmount(cells[layout.amount]) ?? undefined;
			continue;
		}

		if (label.startsWith('Total ')) {
			if (current) current.reportedTotal = parseAmount(cells[layout.amount]) ?? undefined;
			current = null;
			continue;
		}

		if (label && !type) {
			current = { vendorName: label, transactions: [] };
			out.sections.push(current);
			continue;
		}

		if (!current) {
			out.errors.push(`Line ${i + 1}: transaction row outside any vendor section`);
			continue;
		}
		if (type !== 'Bill' && type !== 'Bill Pmt -Check') {
			out.errors.push(`Line ${i + 1}: unsupported type "${type}" — only Bill and Bill Pmt -Check are imported`);
			continue;
		}
		if (!layoutDetected) {
			out.errors.push(`Line ${i + 1}: data row before header — assuming detailed layout`);
		}
		const dateStr = (cells[layout.date] ?? '').trim();
		const dateMs = parseDate(dateStr);
		if (!dateMs) {
			out.errors.push(`Line ${i + 1}: unparseable date "${dateStr}"`);
			continue;
		}
		const amount = parseAmount(cells[layout.amount]);
		if (amount == null) {
			out.errors.push(`Line ${i + 1}: missing amount`);
			continue;
		}
		current.transactions.push({
			type,
			transId: (layout.transId != null ? (cells[layout.transId] ?? '').trim() : '') || undefined,
			dateStr,
			dateMs,
			num: (cells[layout.num] ?? '').trim() || undefined,
			memo: (layout.memo != null ? (cells[layout.memo] ?? '').trim() : '') || undefined,
			account: (layout.account != null ? (cells[layout.account] ?? '').trim() : '') || undefined,
			split: (layout.split != null ? (cells[layout.split] ?? '').trim() : '') || undefined,
			amount,
			amountCents: Math.round(Math.abs(amount) * 100),
		});
	}

	return out;
}
